/**
 * EU-574 — VLibras Widget (v6.0.0), tradutor automático Português → LIBRAS
 * mantido pelo Governo Federal. Integrado no bootstrap do SPA (equivalente a
 * "antes do </body>" do exemplo oficial) para o botão flutuante existir em
 * TODAS as páginas.
 *
 * Resiliência (requisito do card): o `vlibras-plugin.js` é carregado em
 * runtime direto do domínio gov.br — dependência de terceiros fora do nosso
 * controle. Se o script não carregar (rede, gov.br fora, adblock), a
 * aplicação segue 100% funcional: o carregamento é assíncrono (não bloqueia a
 * renderização), o `onerror` remove a marcação órfã e o construtor roda em
 * try/catch — nenhum erro de JS não tratado sobe.
 */

const VLIBRAS_PLUGIN_URL = 'https://vlibras.gov.br/app/vlibras-plugin.js';
const VLIBRAS_APP_URL = 'https://vlibras.gov.br/app';
const CONTAINER_ID = 'vlibras-widget-container';
/**
 * LIB-003 / RN-06 — classe aplicada ao `<html>` enquanto o widget está ABERTO.
 * O CSS global derruba o `pointer-events` dos overlays de stretched-link
 * marcados com `.vlibras-link-passthrough` (cards de vaga/programa/inscrição):
 * o clique alcança o TEXTO por baixo e o VLibras traduz, em vez de oferecer
 * "Acessar link". Widget fechado → classe sai e a navegação volta ao normal.
 */
const WIDGET_OPEN_CLASS = 'vlibras-widget-open';

declare global {
  interface Window {
    VLibras?: {
      Widget: new (appUrl: string) => unknown;
    };
  }
}

/** Marcação oficial do widget (exemplo da documentação do VLibras). */
function createWidgetMarkup(): HTMLDivElement {
  const container = document.createElement('div');
  container.id = CONTAINER_ID;
  container.setAttribute('vw', '');
  container.className = 'enabled';

  const accessButton = document.createElement('div');
  accessButton.setAttribute('vw-access-button', '');
  accessButton.className = 'active';

  const pluginWrapper = document.createElement('div');
  pluginWrapper.setAttribute('vw-plugin-wrapper', '');

  const topWrapper = document.createElement('div');
  topWrapper.className = 'vw-plugin-top-wrapper';

  pluginWrapper.appendChild(topWrapper);
  container.appendChild(accessButton);
  container.appendChild(pluginWrapper);
  return container;
}

/**
 * Anexa o `<script>` ao documento — seam injetável: os testes (happy-dom
 * bloqueia carregamento de script externo com throw síncrono) capturam o
 * elemento sem anexar e disparam `onload`/`onerror` manualmente.
 */
type AppendScript = (script: HTMLScriptElement) => void;

const defaultAppendScript: AppendScript = (script) => {
  document.body.appendChild(script);
};

/**
 * Observa o container do widget e espelha o estado ABERTO na classe
 * `vlibras-widget-open` do `<html>`. O plugin oficial marca o
 * `[vw-plugin-wrapper]` com `.active` quando o usuário abre o widget (e
 * remove ao fechar). Detecção falhando = comportamento atual (sem regressão).
 */
function observeWidgetOpenState(container: HTMLElement): MutationObserver {
  const sync = () => {
    const isOpen = Boolean(container.querySelector('[vw-plugin-wrapper].active'));
    document.documentElement.classList.toggle(WIDGET_OPEN_CLASS, isOpen);
  };
  const observer = new MutationObserver(sync);
  observer.observe(container, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
    childList: true,
  });
  return observer;
}

/**
 * Injeta a marcação + o script oficial do VLibras e instancia o plugin.
 * Idempotente (chamadas repetidas não duplicam o widget) e nunca lança.
 */
export function initVLibras(appendScript: AppendScript = defaultAppendScript): void {
  try {
    if (document.getElementById(CONTAINER_ID)) return;

    const container = createWidgetMarkup();
    document.body.appendChild(container);
    const openStateObserver = observeWidgetOpenState(container);

    const script = document.createElement('script');
    script.src = VLIBRAS_PLUGIN_URL;
    // Assíncrono — nunca bloqueia o parse/render da página.
    script.async = true;

    const disableWidget = (reason: string, err?: unknown) => {
      // gov.br indisponível/bloqueado ou plugin quebrado — a aplicação
      // continua funcional, sem botão órfão e sem erro não tratado.
      console.warn(`[vlibras] ${reason} — widget disabled`, err ?? '');
      openStateObserver.disconnect();
      document.documentElement.classList.remove(WIDGET_OPEN_CLASS);
      container.remove();
      script.remove();
    };

    script.onload = () => {
      try {
        if (window.VLibras) {
          new window.VLibras.Widget(VLIBRAS_APP_URL);
        }
      } catch (err) {
        disableWidget('widget failed to initialize', err);
      }
    };
    script.onerror = () => {
      disableWidget('plugin script failed to load');
    };

    try {
      appendScript(script);
    } catch (err) {
      // Alguns ambientes (CSP restritiva, DOM sandbox) rejeitam o append do
      // script externo com throw SÍNCRONO — mesmo tratamento da falha de rede.
      disableWidget('plugin script append rejected', err);
    }
  } catch (err) {
    // Defesa final: nenhuma falha do widget pode travar o boot do SPA.
    console.warn('[vlibras] init skipped', err);
  }
}
